import { Component, OnInit, ViewChild } from '@angular/core';
import {
  Auth,
  User,
  RecaptchaVerifier,
  signInWithCustomToken,
  isSignInWithEmailLink,
  signInWithEmailLink,
  sendSignInLinkToEmail,
  fetchSignInMethodsForEmail,
  AuthCredential,
  OAuthCredential,
  OAuthProvider,
  sendPasswordResetEmail,
  signInWithPhoneNumber,
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithEmailAndPassword,
  getAdditionalUserInfo,
  signInWithRedirect,
  GoogleAuthProvider,
} from '@angular/fire/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, IonSlides, LoadingController, ModalController, NavController } from '@ionic/angular';

import { environment } from 'src/environments/environment';
import {
  AuthProvider,
  AUTH_PROVIDER_EMAIL_LINK,
  AUTH_PROVIDER_GSUITE,
  AUTH_PROVIDER_MICROSOFT,
  AUTH_PROVIDER_OKTA_OIDC,
  AUTH_PROVIDER_OKTA_SAML,
  AUTH_PROVIDER_PASSWORD,
  AUTH_PROVIDER_PHONE,
} from 'src/models';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { TenantsService } from 'src/services/tenants.service';
import { UploadService } from 'src/services/upload.service';
import { UsersService } from 'src/services/users.service';

import { routes } from '../constants';
import { DomainError } from '../errors';
import { getAppConfig, getCustomFieldId, sleep } from '../utils/utils';

import { PasswordLinkPage } from './password-link/password-link.page';

const PAGE_TYPE_MAIN = 'main';
const PAGE_TYPE_FIND_WORKSPACE = 'findWorkspace';

const INPUT_NUMBER = 'num';
const VERIFY_CODE = 'code';
const NAME = 'name';

const BIZ_EMAIL = 'email';
const BIZ_SIGNUP = 'signup';

const LOGIN_URL_PARAM = 'login';
const CALLBACK_URL_PARAM = 'callback';
const IMPERSONATE_URL_PARAM = 'impersonate';

const CACHED_AUTH_DATA_KEY = 'cachedAuthData';

const CACHED_AUTH_EMAIL = 'cachedAuthEmail';
const CACHED_AUTH_URL = 'cachedAuthUrl';

const slideTypesPersonal = [INPUT_NUMBER, VERIFY_CODE, NAME];

const slideTypesBiz = [BIZ_EMAIL, BIZ_SIGNUP];

const BLOCKING_FUNCTION_ERROR_PREFIX = 'HTTP Cloud Function returned an error: ';
const IDP_ERROR_PREFIX = 'IdP denied access.';

const WARN_ERROR_CODES = ['auth/user-cancelled', 'auth/user-disabled'];

function getAuthErrorMessage(error: any) {
  const message = error.message ?? '';

  if (error.code === 'auth/internal-error' && message.startsWith(BLOCKING_FUNCTION_ERROR_PREFIX)) {
    const errorJson = message.replace(`${BLOCKING_FUNCTION_ERROR_PREFIX}`, '');
    const errorObj = JSON.parse(errorJson);

    return errorObj.error.message;
  }

  if (error.code === 'auth/user-cancelled' && message.startsWith(IDP_ERROR_PREFIX)) {
    return 'You may not be authorized to login by your SSO provider. Please contact your administrator.';
  }

  return message;
}

@Component({
  selector: 'app-auth',
  templateUrl: './auth.page.html',
  styleUrls: ['./auth.page.scss'],
})
export class AuthPage implements OnInit {
  @ViewChild(IonSlides, { static: false }) slides: IonSlides;
  @ViewChild('phoneInput', { static: false }) phoneInput;
  @ViewChild('emailInput', { static: false }) emailInput;
  @ViewChild('codeInput', { static: false }) codeInput;
  @ViewChild('passwordInput', { static: false }) passwordInput;
  @ViewChild('firstNameInput', { static: false }) firstNameInput;
  @ViewChild('lastNameInput', { static: false }) lastNameInput;

  countries = [
    {
      name: 'United States',
      emoji: '🇺🇸',
      prefix: '+1',
    },
    {
      name: 'United Kingdom',
      emoji: '🇬🇧',
      prefix: '+44',
    },
    {
      name: 'Australia',
      emoji: '🇦🇺',
      prefix: '+61',
    },
    {
      name: 'Canada',
      emoji: '🇨🇦',
      prefix: '+1',
    },
    {
      name: 'France',
      emoji: '🇫🇷',
      prefix: '+33',
    },
    {
      name: 'Germany',
      emoji: '🇩🇪',
      prefix: '+49',
    },
    {
      name: 'Ireland',
      emoji: '🇮🇪',
      prefix: '+353',
    },
    {
      name: 'Sweden',
      emoji: '🇸🇪',
      prefix: '+46',
    },
  ];

  phone = '';
  code = '';
  firstName = '';
  lastName = '';
  email = '';
  password = '';
  passwordConfirm = '';
  customSignupFields = [];

  user: User;

  isLoading = true;
  isAcceptingTerms = true;
  isInvalidSubdomain = false;

  // Logging can be phone, email, or SSO, and we handle both signup/login on this page
  isPhoneAuth = false;
  accountExists = false;
  provider?: string;

  // Phone number login
  isPhoneConfirmed = false;
  hasSentVerification = false;
  countryCode = this.countries[0].prefix;

  // These only exist if we're redirecting after login for a certain action
  redirect: string;

  subdomain = 'app';

  title = 'Get started';
  numberSubtitle = 'Use your phone number';
  nameSubtitle = 'Only your name will be visible to others.';
  buttonTitle = 'Finish Signing In';

  page = PAGE_TYPE_FIND_WORKSPACE;

  showThirdPartyCookieWarning = false;

  get isPublicSite() {
    return this.subdomain === 'app';
  }

  get isFindWorkspacePage() {
    return this.page === PAGE_TYPE_FIND_WORKSPACE;
  }

  get isMainPage() {
    return this.page === PAGE_TYPE_MAIN;
  }

  constructor(
    private msgSrvc: MessageService,
    private modalCtrl: ModalController,
    private navController: NavController,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private router: Router,
    private route: ActivatedRoute,
    public authService: AuthService,
    private uploadSrvc: UploadService,
    public afa: Auth,
    private tenantService: TenantsService,
    private usersService: UsersService,
    private analyticsService: AnalyticsService,
    private apiService: ApiService,
  ) {}

  async ngOnInit() {
    this.user = this.authService.user;
    this.subdomain = this.authService.subdomain;
    this.redirect = this.route.snapshot.queryParamMap.get('redirect');
    const isMobile = this.route.snapshot.queryParamMap.get('mobile') === 'true';
    this.email = localStorage.getItem(CACHED_AUTH_EMAIL) ?? '';
    const url = localStorage.getItem(CACHED_AUTH_URL) ?? '';
    this.isInvalidSubdomain = !this.authService?.authProviders?.length;

    if (this.isPublicSite) {
      this.page = PAGE_TYPE_FIND_WORKSPACE;
    } else {
      this.page = PAGE_TYPE_MAIN;
    }

    this.route.queryParams.subscribe((params) => {
      if (params.page) {
        this.page = params.page;
      }
    });

    if (isMobile && url && window.location.href !== url) {
      window.location.href = url;
    }

    this.customSignupFields = this.authService.customProfileFields.filter((f) => {
      return f.type !== 'user-attribute';
    });

    window['recaptchaVerifier'] = new RecaptchaVerifier(await this.afa, 'recaptcha-container', {
      size: 'invisible',
      callback: (response) => {
        console.log('Captcha response: ', response);
        // reCAPTCHA solved, allow signInWithPhoneNumber.

        if (!this.hasSentVerification) {
          this.sendVerificationText();
          this.hasSentVerification = true;
        }
      },
    });

    // Slides are undefined when view is ready because ionic sucks
    setTimeout(() => {
      this.slides.lockSwipes(true);

      if (this.isPhoneAuth) {
        this.focusPhoneInput();
      } else if (this.hasPasswordAuthProvider()) {
        this.focusEmailInput();
      }
    }, 500);

    try {
      // Get flags that may have been set on previous page load.
      const wasAutoSignIn = this.authService.getAutoSignInFlag();
      const wasManualLogout = this.authService.getWasManualLogoutFlag();
      const authError = this.authService.getAuthError();

      // If there was an error fetching the user, show an error message.
      if (authError) {
        throw new DomainError(authError.message, { code: authError.code });
      }

      // Auth provider param will be provided during oauth process
      const authProviderParam = this.route.snapshot.paramMap.get('authProvider');
      this.provider = authProviderParam;

      if (authProviderParam === IMPERSONATE_URL_PARAM) {
        const customToken = await this.authService.getCustomToken(window.opener);

        await signInWithCustomToken(this.afa, customToken);

        window.location.href = `${window.location.origin}/${LOGIN_URL_PARAM}`;
        return;
      }

      if (authProviderParam === CALLBACK_URL_PARAM) {
        let userCred;

        const result = await isSignInWithEmailLink(this.afa, window.location.href);

        if (result) {
          const [email, pendingCred] = this.getCachedAuthData();

          userCred = await signInWithEmailLink(this.afa, email, window.location.href);

          if (pendingCred) {
            await userCred.user.linkWithCredential(pendingCred);
          }
        } else {
          userCred = this.authService.credential;
        }

        if (userCred && userCred.user) {
          return this.didFinishSSO();
        }
      }

      if (!this.user) {
        if (authProviderParam === AUTH_PROVIDER_OKTA_OIDC) {
          return this.authOktaOidc();
        } else if (authProviderParam === AUTH_PROVIDER_OKTA_SAML) {
          return this.authOktaSaml();
        } else if (this.getGenericSSOProvider() && authProviderParam === this.getGenericSSOProvider()) {
          return this.authGenericSSO();
        }

        // If we arrive at the login screen and are trying to be redirected,
        // we'd want to auto-auth them if we know what auth method to use (aka there's only one enabled)
        // Otherwise, the user would arrive here and just see a single "Login" button
        // And they'd think to themselves, "why the hell do I have to click this button
        // when it's the only thing on this whole goddamn page?!?"
        // Nay nay, we're benevolent developers and will save them that struggle
        if (
          !wasManualLogout &&
          this.redirect &&
          this.authService.authProviders.length === 1 &&
          !this.showThirdPartyCookieWarning
        ) {
          // If we previously attempted to auto sign them in and ended up back here, don't reattempt
          // it because it will probably trigger an infinite redirect loop.
          if (wasAutoSignIn) {
            throw new Error(
              'You may need to enable third-party cookies for your identity provider to log you in. Please make sure third-party cookies are enabled in your browser settings and try logging in again.',
            );
          } else {
            this.authService.setAutoSigninFlag();

            if (this.hasOktaOidcAuthProvider()) {
              return this.authOktaOidc();
            } else if (this.hasOktaSamlAuthProvider()) {
              return this.authOktaSaml();
            } else if (this.hasGoogleAuthProvider()) {
              return this.authGoogle();
            } else if (this.hasMicrosoftAuthProvider()) {
              return this.authMicrosoft();
            } else if (this.getGenericSSOProvider()) {
              return this.authGenericSSO();
            } else if (this.hasPhoneAuthProvider()) {
              this.isPhoneAuth = true;
            }
          }
        }
      } else {
        this.finishAuth();
      }
    } catch (error) {
      console.log('Error handling redirect for auth: ', error);

      const onFailure = (err) => {
        this.msgSrvc.show(
          `We couldn't log you in. ${
            getAuthErrorMessage(err) || 'Please try again or contact your support representative.'
          }`,
          {
            duration: 10000,
          },
        );

        this.apiService.postPromise('/log-entries', {
          level: WARN_ERROR_CODES.includes(error.code) ? 'warn' : 'error',
          data: {
            message: err.message,
            stack: err.stack,
            code: err.code,
            appConfig: getAppConfig(),
          },
        });
      };

      // Note: we may have multiple providers for a single user (error.code === 'auth/account-exists-with-different-credential')
      if (error.code === 'auth/account-exists-with-different-credential') {
        const pendingCred = error.credential;
        const email = error.email;
        const methods = await fetchSignInMethodsForEmail(this.afa, email);

        if (!methods.length || [AUTH_PROVIDER_PASSWORD, AUTH_PROVIDER_EMAIL_LINK].includes(methods[0])) {
          const actionCodeSettings = {
            url: `${window.location.origin}/${LOGIN_URL_PARAM}/${CALLBACK_URL_PARAM}`,
            handleCodeInApp: true,
          };

          this.setCachedAuthData(email, pendingCred);

          await sendSignInLinkToEmail(this.afa, email, actionCodeSettings);

          const modal = await this.modalCtrl.create({
            component: PasswordLinkPage,
          });

          await modal.present();
        } else {
          onFailure(error);
        }
      } else {
        onFailure(error);
      }
    }

    this.isLoading = false;
  }

  async determineThirdPartyCookieSupport() {
    while (getAppConfig().thirdPartyCookiesEnabled === undefined) {
      await sleep(500);
    }

    return getAppConfig().thirdPartyCookiesEnabled;
  }

  changePage(page: string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page,
      },
      queryParamsHandling: 'merge',
    });
  }

  focusFirstNameInput() {
    if (this.firstNameInput) {
      this.firstNameInput.setFocus();
    }
  }

  focusEmailInput() {
    if (this.emailInput) {
      this.emailInput.setFocus();
    }
  }

  focusPhoneInput() {
    if (this.phoneInput) {
      this.phoneInput.setFocus();
    }
  }

  focusCodeInput() {
    if (this.codeInput) {
      this.codeInput.setFocus();
    }
  }

  focusLastNameInput() {
    if (this.lastNameInput) {
      this.lastNameInput.setFocus();
    }
  }

  async slideChanged() {
    const currentIndex = await this.slides.getActiveIndex();

    if (this.isPhoneAuth) {
      const slide = slideTypesPersonal[currentIndex];
      if (slide === INPUT_NUMBER) {
        this.focusPhoneInput();
      } else if (slide === VERIFY_CODE) {
        this.focusCodeInput();
      } else if (slide === NAME) {
        this.focusFirstNameInput();

        if (window['comfirmationResult']) {
          // We've verified the phone, so no more changes
          this.slides.lockSwipes(true);
        }
      }
    } else {
      const slide = slideTypesBiz[currentIndex];

      if (slide === BIZ_SIGNUP && !this.provider) {
        this.focusFirstNameInput();
      }
    }
  }

  async fetchSubdomainForEmail() {
    const emailDomain = this.email.split('@')[1];
    try {
      this.isLoading = true;
      const { subdomain } = await this.tenantService.getByEmailDomain(emailDomain);
      this.isLoading = false;
      localStorage.setItem(CACHED_AUTH_EMAIL, this.email);
      let url = '';
      if (environment.production) {
        url = `https://${subdomain}.${environment.rootDomain}`;
      } else {
        url = `${environment.baseUrl}?subdomain=${subdomain}`;
      }
      localStorage.setItem(CACHED_AUTH_URL, url);
      window.location.href = url;
    } catch (err) {
      this.isLoading = false;
      this.isInvalidSubdomain = true;
      this.changePage('main');
      // Log to analytics
      this.analyticsService.trackEvent('Auth', 'No Workspace Found', { email: this.email, error: err });
    }
  }

  async next() {
    const currentIndex = await this.slides.getActiveIndex();

    if (this.isPhoneAuth) {
      const slide = slideTypesPersonal[currentIndex];

      if (slide === INPUT_NUMBER) {
        const phone = `${this.phone}`.replace(/[^\d]/g, '');

        if (phone.length > 7) {
          this.sendVerificationText();
        } else {
          this.msgSrvc.show('Whoops, you forgot to add your number.');
          setTimeout(() => {
            this.focusPhoneInput();
          }, 1000);
        }
      } else if (slide === VERIFY_CODE) {
        const code = `${this.code}`;

        if (code.length) {
          this.verifyCode();
        } else {
          this.msgSrvc.show('Please enter the verification code.');

          setTimeout(() => {
            this.focusCodeInput();
          }, 1000);
        }
      } else if (slide === NAME) {
        if (!this.isAcceptingTerms) {
          this.msgSrvc.show("You can't use the app without agreeing to terms.");

          setTimeout(() => {
            this.focusFirstNameInput();
          }, 1000);

          return;
        }

        if (!this.firstName.length) {
          this.msgSrvc.show('So close! Please add your name.');

          setTimeout(() => {
            this.focusFirstNameInput();
          }, 1000);
        } else if (!this.lastName.length) {
          this.focusLastNameInput();

          this.msgSrvc.show('Almost! Please add your last name.');

          setTimeout(() => {
            this.focusFirstNameInput();
          }, 1000);
        } else if (!this.email.length) {
          this.msgSrvc.show('Almost! Please add your email.');

          setTimeout(() => {
            this.focusFirstNameInput();
          }, 1000);
        } else {
          await this.updateProfile(true);
        }
      }
    } else {
      const slide = slideTypesBiz[currentIndex];

      if (slide === BIZ_EMAIL) {
        this.loginEmailPassword(true);
      }
    }
  }

  nextSlide() {
    this.slides.lockSwipes(false);
    this.slides.slideNext();
    this.slides.lockSwipes(true);
  }

  setSlideIndex(index: number) {
    this.slides.lockSwipes(false);
    this.slides.slideTo(index);
    this.slides.lockSwipes(true);
  }

  back() {
    this.slides.lockSwipes(false);
    this.slides.slidePrev();
    this.slides.lockSwipes(true);
  }

  openToS() {
    window.open('https://bit.ly/turnout-terms', '_system', 'location=yes');
  }

  sendVerificationText() {
    this.isLoading = true;
    this.slides.lockSwipes(this.isLoading);

    const appVerifier = window['recaptchaVerifier'];

    this.hasSentVerification = true;

    const phone = `${this.countryCode}${this.phone.replace(/[^\d]/g, '')}`;

    signInWithPhoneNumber(this.afa, phone, appVerifier)
      .then((result) => {
        window['confirmationResult'] = result;

        this.isLoading = false;
        this.slides.lockSwipes(false);
        this.setSlideIndex(slideTypesPersonal.indexOf(VERIFY_CODE));
      })
      .catch((error) => {
        this.isLoading = false;
        this.slides.lockSwipes(false);

        this.msgSrvc.show(getAuthErrorMessage(error));
        console.log('Error sending verification: ', error);
      });
  }

  verifyCode() {
    this.isLoading = true;
    this.slides.lockSwipes(this.isLoading);

    const code = `${this.code}`;

    const confResult = window['confirmationResult'];

    confResult
      .confirm(code)
      .then(async (result) => {
        this.isPhoneConfirmed = true;
        this.isLoading = false;
        this.slides.lockSwipes(false);
        this.user = result.user;

        await this.authService.refreshUser(this.user);

        if (this.user.displayName) {
          this.finishAuth(true);
        } else {
          this.setSlideIndex(slideTypesPersonal.indexOf(NAME));
        }
      })
      .catch((error) => {
        this.isLoading = false;
        this.slides.lockSwipes(false);

        this.msgSrvc.show(getAuthErrorMessage(error));
        console.log(error, 'Incorrect code entered?');
      });
  }

  createBizAccount() {
    for (const req of this.customSignupFields) {
      if (req.value === undefined) {
        req.value = null;
      }

      if (req.required && !req.value) {
        this.msgSrvc.show('Please fill out all required fields');
        return;
      }
    }

    if (this.provider) {
      // We're logging in through SSO and saving user details from second screen
      this.updateProfile();
    } else {
      // This means we're logging in with an email/password
      // Basic field validation
      if (!this.firstName || !this.lastName) {
        this.msgSrvc.show('Please add a first and last name');
        return;
      }

      if (!this.email) {
        this.msgSrvc.show('Please add a valid email address');
        return;
      }

      if (!this.password || this.password.length < 8) {
        this.msgSrvc.show('Please add a password of at least 8 characters');
        return;
      }

      if (this.password !== this.passwordConfirm) {
        this.msgSrvc.show('Passwords do not match');
        return;
      }

      // Check if a user exists, meaning we tried to sign up but failed partly through the process
      if (this.user && this.authService.isEmailVerified) {
        this.updateProfile();

        return;
      }

      this.isLoading = true;

      createUserWithEmailAndPassword(this.afa, this.email, this.password)
        .then(({ user }) => {
          return Promise.all([
            this.authService.sendVerificationEmailForUser(user),
            updateProfile(user, {
              displayName: `${this.firstName} ${this.lastName}`,
            }),
          ]);
        })
        .then(() => {
          // Show a message indicating an email has been sent.
          return this.finishAuth(true);
        })
        .catch((error) => {
          this.isLoading = false;
          this.msgSrvc.show(getAuthErrorMessage(error));
        });
    }
  }

  hasAuthProvider(provider: AuthProvider) {
    return this.authService.authProviders && this.authService.authProviders.includes(provider);
  }

  hasPasswordAuthProvider() {
    return this.hasAuthProvider(AUTH_PROVIDER_PASSWORD);
  }

  hasPhoneAuthProvider() {
    return this.hasAuthProvider(AUTH_PROVIDER_PHONE);
  }

  hasGoogleAuthProvider() {
    return this.hasAuthProvider(AUTH_PROVIDER_GSUITE);
  }

  hasOktaOidcAuthProvider() {
    return this.hasAuthProvider(AUTH_PROVIDER_OKTA_OIDC);
  }

  hasOktaSamlAuthProvider() {
    return this.hasAuthProvider(AUTH_PROVIDER_OKTA_SAML);
  }

  hasMicrosoftAuthProvider() {
    return this.hasAuthProvider(AUTH_PROVIDER_MICROSOFT);
  }

  getGenericSSOProvider(): string | null {
    for (const provider of this.authService.authProviders) {
      if (provider.startsWith('oidc.') || provider.startsWith('saml.')) {
        return provider;
      }
    }
    return null;
  }

  loginEmailPassword(isTest: boolean) {
    this.analyticsService.trackEvent('Auth', 'Login', { provider: 'password' });

    this.isLoading = true;

    // Try to log them in a dummy password to see if the account exists
    signInWithEmailAndPassword(this.afa, this.email, isTest ? 'T0EWNFaWU5Df2KCUWbqjhzL2RHRC2' : this.password)
      .then(async (result) => {
        const isEmailVerified =
          getAdditionalUserInfo(result).providerId === 'password' ? result.user.emailVerified : true;

        if (isEmailVerified) {
          this.user = result.user;

          await this.authService.refreshUser(this.user);

          return this.updateProfile(true);
        }

        return this.finishAuth(true);
      })
      .catch((error) => {
        this.isLoading = false;

        console.log('Unable to login: ', error);

        if (isTest && error.code === 'auth/wrong-password') {
          this.accountExists = true;
          this.nextSlide();
        } else if (error.code === 'auth/user-not-found') {
          if (isTest) {
            this.accountExists = false;
            this.nextSlide();
          } else {
            // Show a special message because google's wording sucks
            this.msgSrvc.show('No account exists for that email address.');
          }
        } else {
          this.msgSrvc.show(getAuthErrorMessage(error));
        }
      });
  }

  loginWithProvider(provider) {
    this.analyticsService.trackEvent('Auth', 'Login', { provider });

    window.history.pushState({}, '', `/${LOGIN_URL_PARAM}/${CALLBACK_URL_PARAM}${window.location.search}`);

    return signInWithRedirect(this.afa, provider);
  }

  authOktaOidc() {
    const provider = new OAuthProvider('oidc.okta');

    this.loginWithProvider(provider);
  }

  authOktaSaml() {
    const provider = new OAuthProvider('saml.okta');

    this.loginWithProvider(provider);
  }

  authMicrosoft() {
    const provider = new OAuthProvider('microsoft.com');

    this.loginWithProvider(provider);
  }

  authGenericSSO() {
    const provider = new OAuthProvider(this.getGenericSSOProvider());

    this.loginWithProvider(provider);
  }

  authGoogle() {
    const provider = new GoogleAuthProvider();

    provider.addScope('profile');
    provider.addScope('email');
    provider.setCustomParameters({
      access_type: 'offline',
    });

    this.loginWithProvider(provider);
  }

  async forgotPassword() {
    const alert = await this.alertCtrl.create({
      header: 'Forgot password',
      subHeader: "Enter your email address and we'll send you a link to reset",
      inputs: [
        {
          name: 'email',
          type: 'email',
          value: this.email,
          placeholder: 'name@email.com',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            console.log('Confirm Cancel');
          },
        },
        {
          text: 'Send',
          handler: async (data) => {
            const email = data.email;

            sendPasswordResetEmail(this.afa, email)
              .then(() => {
                this.msgSrvc.show('Check your email for a reset link.');
              })
              .catch((err) => {
                this.msgSrvc.show('No email found.');
                console.error('[ForgotPassword]: ', err.message);
              });
          },
        },
      ],
    });

    await alert.present();
  }

  async updateProfile(fullRefresh = false) {
    if (!this.isPhoneConfirmed && this.isPhoneAuth) {
      this.msgSrvc.show('Please confirm your number.');
      this.setSlideIndex(slideTypesPersonal.indexOf(VERIFY_CODE));
      return;
    }

    const user = this.user;
    const name = user?.displayName ?? `${this.firstName} ${this.lastName}`;

    this.isLoading = true;

    try {
      const profileUpdate: any = {
        name,
      };

      if (this.phone) {
        profileUpdate.phoneNumber = this.phone;
      }

      if (this.customSignupFields && this.customSignupFields.length) {
        profileUpdate.customFields = this.customSignupFields;
      }

      await this.usersService.updateUser(this.authService.userId, profileUpdate);

      if (this.isPhoneAuth) {
        await this.usersService.updateEmail(this.authService.userId, this.email, user);
      }

      this.isLoading = false;
      this.finishAuth(fullRefresh);
    } catch (error) {
      this.isLoading = false;

      if (this.isPhoneAuth) {
        this.slides.lockSwipes(false);
      }

      this.msgSrvc.show(`There was a problem: ${error.message}`);
      console.log('Error saving user profile: ', error);
    }
  }

  didFinishSSO() {
    this.isLoading = false;

    this.customSignupFields.forEach((defaultField) => {
      if (this.authService.profile && this.authService.profile.customFields) {
        this.authService.profile.customFields.forEach((field) => {
          if (getCustomFieldId(defaultField) === getCustomFieldId(field)) {
            // Take the value given, but any other metadata (like required or visible) should inherit from company setting.
            defaultField.value = field.value;
          }
        });
      }
    });

    // If there are required profile fields, let's ask them to fill them out
    let hasRequiredSignupFields = false;

    for (const req of this.customSignupFields) {
      if (req.required && !req.value) {
        hasRequiredSignupFields = true;
      }
    }

    if (hasRequiredSignupFields) {
      this.nextSlide();
    } else {
      this.finishAuth();
    }
  }

  finishAuth(fullRefresh = false) {
    this.analyticsService.trackEvent('Auth', 'Finished Login');

    if (fullRefresh) {
      return window.location.reload();
    }

    this.navController.navigateRoot(this.redirect || routes.DASHBOARD);
  }

  async detectProfilePhotoUpload(event) {
    const files = event.target.files;

    if (files.length) {
      const file = event.target.files.item(0);

      const loader = await this.loadingCtrl.create({
        message: 'Uploading...',
        spinner: 'crescent',
        duration: 10000,
      });

      loader.present();

      try {
        await this.uploadSrvc.uploadProfilePicture(this.authService.userId, file);
      } catch (err) {
        this.msgSrvc.show(err.message);
      } finally {
        loader.dismiss();
      }
    }
  }

  setCachedAuthData(email: string, cred?: AuthCredential) {
    let cachedAuthData = email;

    if (cred) {
      cachedAuthData += `:${btoa(JSON.stringify(cred))}`;
    }

    localStorage.setItem(CACHED_AUTH_DATA_KEY, cachedAuthData);
  }

  getCachedAuthData(): [string, AuthCredential?] {
    const cachedAuthData = localStorage.getItem(CACHED_AUTH_DATA_KEY);
    let email: string | undefined;
    let credJson: string | undefined;
    let cred: AuthCredential | undefined;

    if (cachedAuthData) {
      localStorage.removeItem(CACHED_AUTH_DATA_KEY);
      [email, credJson] = cachedAuthData.split(':');

      if (credJson) {
        cred = OAuthCredential.fromJSON(atob(credJson));
      }
    }

    if (!email) {
      throw new Error();
    }

    return [email, cred];
  }

  openMarketingSignupPage() {
    window.open(`https://unthread.io/demo`, '_blank');
  }
}
